import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { format, isValid, subYears } from 'date-fns';
import {
  getCurrentLanguage,
  getText,
} from '@storebrand-digital/fms-translations';
import {
  DatePicker,
  DatePickerSelectedValue,
} from '@storebrand-digital/pretzel';
import { filterActions } from './reducer';
import { useAppDispatch, useAppSelector } from '../../app/store';
import { periodFormValidationSchema } from './Filter.Form.Period.schemas';
import styles from './Filter.Form.Period.module.css';
import { useDocumentContext } from '../Document/context';
import { Segment } from '../../utils/segments';

function FilterFormPeriod() {
  const dispatch = useAppDispatch();
  const createdFrom = useAppSelector((state) => state.filter.createdFrom);
  const createdTo = useAppSelector((state) => state.filter.createdTo);
  const { selectedSegmentId } = useDocumentContext();

  // on segment changes, update the key to force Formik to re-initialize
  const [key, setKey] = useState(0);
  useEffect(() => {
    setKey((prevKey) => prevKey + 1);
  }, [createdFrom, createdTo, selectedSegmentId]);

  const createdFromDate = createdFrom
    ? new Date(createdFrom)
    : selectedSegmentId === Segment.BANK
      ? subYears(new Date(), 1)
      : undefined;
  const createdToDate = createdTo
    ? new Date(createdTo)
    : selectedSegmentId === Segment.BANK
      ? new Date()
      : undefined;

  const formik = useFormik({
    initialValues: {
      createdFromDate: createdFromDate,
      createdToDate: createdToDate,
    },
    enableReinitialize: true,
    validationSchema: periodFormValidationSchema,
    onSubmit() {},
  });

  const { values, touched, errors, setFieldValue, setFieldTouched } = formik;

  const handleDateChange = (
    field: 'createdFromDate' | 'createdToDate',
    selectedValue: DatePickerSelectedValue | undefined
  ) => {
    if (!selectedValue) {
      // when date is cleared
      setFieldValue(field, null);
    } else if (selectedValue.isValid) {
      setFieldValue(field, selectedValue.date);
    } else {
      // invalid date input
      setFieldValue(field, null);
    }
  };

  useEffect(() => {
    if (values.createdFromDate && isValid(values.createdFromDate)) {
      const newCreatedFrom = format(values.createdFromDate, 'yyyy-MM-dd');
      dispatch(filterActions.changeCreatedFrom(newCreatedFrom));
    } else {
      dispatch(filterActions.deselectCreatedFrom());
    }

    if (values.createdToDate && isValid(values.createdToDate)) {
      const newCreatedTo = format(values.createdToDate, 'yyyy-MM-dd');
      dispatch(filterActions.changeCreatedTo(newCreatedTo));
    } else {
      dispatch(filterActions.deselectCreatedTo());
    }
  }, [dispatch, values.createdFromDate, values.createdToDate]);

  const createdFromAlertMessage = touched.createdFromDate
    ? (errors.createdFromDate as string)
    : undefined;
  const createdFromAlert = createdFromAlertMessage
    ? { type: 'error' as const, message: createdFromAlertMessage }
    : undefined;
  const createdToAlertMessage = touched.createdToDate
    ? (errors.createdToDate as string)
    : undefined;
  const createdToAlert = createdToAlertMessage
    ? { type: 'error' as const, message: createdToAlertMessage }
    : undefined;
  const clearable = selectedSegmentId === Segment.MAIN;

  return (
    <div className={styles.filterFormPeriodBase} key={key}>
      <div className={styles.container}>
        <DatePicker
          id="createdFromDate"
          name="createdFromDate"
          className="createdFromDatePicker"
          aria-label="createdFromDate"
          selectMonthAriaLabel="Select month"
          selectYearAriaLabel="Select year"
          label={getText('fromDate')}
          data-trackid="button-dropdownfilter_from_date|Velg fra dato"
          value={values.createdFromDate}
          onValueChange={(value) =>
            handleDateChange('createdFromDate', value)
          }
          onBlur={() => setFieldTouched('createdFromDate', true)}
          alert={createdFromAlert}
          locale={getCurrentLanguage() === 'en' ? 'en' : 'no'}
          format="dd.MM.yyyy"
          clearable={clearable}
          autoformat
          fluid
        />
        <DatePicker
          id="createdToDate"
          name="createdToDate"
          aria-label="createdToDate"
          className="createdToDatePicker"
          selectMonthAriaLabel="Select month"
          selectYearAriaLabel="Select year"
          label={getText('toDate')}
          data-trackid="button-dropdownfilter_to_date|Velg til dato"
          value={values.createdToDate}
          onValueChange={(value) => handleDateChange('createdToDate', value)}
          onBlur={() => setFieldTouched('createdToDate', true)}
          alert={createdToAlert}
          locale={getCurrentLanguage() === 'en' ? 'en' : 'no'}
          format="dd.MM.yyyy"
          clearable={clearable}
          autoformat
          fluid
        />
      </div>
    </div>
  );
}

export default FilterFormPeriod;
